import { useFlags } from '@features/feature-flags';
import { PageHeader, TabStrip } from '@shared/ui';
import { Outlet } from 'react-router';

import { useComponents } from '../hooks/use-components';

const BASE = '/catalog';

/**
 * What exists on the platform, with no tenant in sight.
 *
 * The other half of the split this screen exists for is `Client → …`, which
 * says what one museum has. Keeping the catalog and the assignment on separate
 * screens is what Stripe does with products and subscriptions, Auth0 with APIs
 * and user grants, and LaunchDarkly with flags and targeting: in all three the
 * thing and who has it never share a page, because they are edited by different
 * people for different reasons.
 *
 * No tab is the index. Every one has its own path, so `/catalog` is a real URL
 * with nothing to mark — which is why the router redirects it to
 * `/catalog/flags` rather than rendering a strip with no tab lit.
 *
 * The counts sit on the tabs rather than on the sidebar row. A number beside
 * `Catalog` would have to stand for three things at once and would read as
 * whichever one the reader had in mind; beside `Flags` it means flags. They
 * share their query with the list below — the same `useFlags` and
 * `useComponents` React Query caches the tabs open onto — so a tab and the table
 * it opens cannot disagree.
 */
export function CatalogLayout() {
    const flags = useFlags();
    const components = useComponents();

    return (
        <>
            <PageHeader
                eyebrow="Configuration"
                title="Catalog"
                description="Everything the platform can grant a museum. Flags are the switches themselves; components and products group them into things Product and Sales can talk about."
            />
            <TabStrip
                label="Catalog sections"
                base={BASE}
                tabs={[
                    { to: `${BASE}/flags`, label: 'Flags', count: flags.data?.length },
                    // Real now. The literal `0` that stood here was true while
                    // no component catalog existed — the screen existed before
                    // it held anything, and omitting the number would have read
                    // as "not counted yet" rather than "none". There is a
                    // catalog to count, so it is counted, and from the same
                    // query the tab opens onto: `undefined` while it loads, so
                    // the strip shows nothing rather than claiming zero.
                    {
                        to: `${BASE}/components`,
                        label: 'Components',
                        count: components.data?.length,
                    },
                    { to: `${BASE}/products`, label: 'Products', count: 0 },
                ]}
            />
            <Outlet />
        </>
    );
}
